import { z } from 'zod';

import { refineBidDayRange } from './requests/carriers.js';

// WHY (#1060): the bid-unit enum literal lives in
//   `russia-request-carriers.ts`, but that file already imports the
//   scope schema from THIS module, so re-importing the unit enum here
//   would close a circular module graph (zod runtime panics on the
//   `.default(...)` call against an undefined re-export). Mirror the
//   enum literal locally instead — the union of values is pinned by the
//   shared test in russia-request-carriers.test.ts.
const RussiaRequestPrimaryBidSummaryUnitSchema = z.enum(['total', 'kg', 'm3', 'piece']);

/**
 * @aiPurpose Russia-specific carrier-bid scope. Disjoint from the China
 *   `CarrierBidScopeSchema` (`REQUEST | ITEM | BOX | BOX_ITEM | BOX_GROUP`).
 *   Russia bids only ever cover the whole request or one start-city.
 *   Mirrors the Prisma `RussiaCarrierBidScope` enum under enum-sync.sh.
 */
export const RussiaCarrierBidScopeSchema = z.enum(['REQUEST', 'START_CITY']);
export type RussiaCarrierBidScope = z.infer<typeof RussiaCarrierBidScopeSchema>;

/**
 * @aiPurpose denormalized list-row summary of the current primary /
 *   winner bid for a `RussiaRequest`. Embedded inside `RussiaRequestSchema`
 *   so the list page renders the column without a per-row carriers fetch.
 *   Resolution rules (see `toWireRussiaRequest`):
 *     1. `winnerBidId` is non-null AND resolves to a hydrated bid (any
 *        scope) → that bid.
 *     2. `winnerBidId` IS strictly null AND exactly one REQUEST-scope
 *        bid exists across all attached carriers → that bid.
 *     3. Else (no bids, multiple REQUEST-scope primaries, or non-null
 *        `winnerBidId` that fails to resolve) → `null`. No fallback into
 *        case 2 when `winnerBidId` is set-but-stale.
 * @aiInvariant `scope` carries the source bid's scope so the UI can label
 *   START_CITY-overridden winners distinctly; current semantics in #868
 *   only ever surface REQUEST-scope unless `winnerBidId` was explicitly
 *   set to a START_CITY bid (no UI sets this yet).
 */
export const RussiaRequestPrimaryBidSummarySchema = z
  .object({
    id: z.string().uuid(),
    carrierId: z.string().min(1),
    carrierName: z.string().min(1),
    price: z.number(),
    currency: z.string(),
    // WHY (#1060): list/mobile summary needs the unit so it can render a
    //   localized suffix (`/кг`, `/шт`, etc) consistent with the carrier
    //   card. Defaults to `'total'` for legacy rows that pre-date the
    //   range/unit slice — same convention used by the carrier bid
    //   schema (RussiaRequestCarrierBidSchema accepts `'total'` as the
    //   wire default).
    unit: RussiaRequestPrimaryBidSummaryUnitSchema,
    daysMin: z.number().int().positive().nullable(),
    daysMax: z.number().int().positive().nullable(),
    scope: RussiaCarrierBidScopeSchema,
  })
  .strict()
  // WHY (#1051): the list-row summary derives from the same bid row as
  //   `RussiaRequestCarrierBidSchema`, so the same both-or-neither +
  //   `daysMin <= daysMax` invariant applies. Enforcing it here catches
  //   a projector drifting away from the underlying bid.
  .superRefine((summary, ctx) => {
    refineBidDayRange(summary.daysMin, summary.daysMax, ctx);
  });
export type RussiaRequestPrimaryBidSummary = z.infer<typeof RussiaRequestPrimaryBidSummarySchema>;

/**
 * @aiPurpose identity-free logistics-cost projection for a `RussiaRequest`
 *   (#1548). Resolves from the SAME winner/single-REQUEST-bid selection as
 *   `RussiaRequestPrimaryBidSummarySchema`, but carries ONLY the money +
 *   validity figures — no carrier id / name / contacts — so a Manager who
 *   lacks `russiaCarriers.read` can see the cost WITHOUT any carrier
 *   identity. Always present on the wire (nullable, never PII-gated).
 * @aiSecurity by construction this object has NO carrier-identity field.
 *   The wire layer emits it for ALL audiences; the PII wall on
 *   `primaryBidSummary` stays — never add an id/name/contacts field here.
 */
export const RussiaRequestPrimaryCostSummarySchema = z
  .object({
    price: z.number(),
    currency: z.string(),
    unit: RussiaRequestPrimaryBidSummaryUnitSchema,
    validityDays: z.number().int().positive(),
    expiresAt: z.string().datetime(),
    isExpired: z.boolean(),
  })
  .strict();
export type RussiaRequestPrimaryCostSummary = z.infer<typeof RussiaRequestPrimaryCostSummarySchema>;
